import { Injectable } from "@angular/core";
import { InspectionJob, JobIssue, JobIssueVendor, Lot, Media, UserVendorContact, Zone, Area, Assertion, AssertionIssue, Component, JobSignature, SyncableStatus } from "../../../core/database/db";
import { AreaDTOSyncable } from "../../../domains/areas/models/AreaDTO";
import { AssertionDTOSyncable } from "../../../domains/assertions/models/AssertionDTO";
import { ComponentDTOSyncable } from "../../../domains/components/models/ComponentDTO";
import { InspectionJobDTOSyncable } from "../../../domains/jobs/models/InspectionJobDTO";
import { JobIssueDTOSyncable, AssertionIssueDTOSyncable } from "../../../domains/jobIssues/models/IssueDTO";
import { LotAssetDtoSyncable } from "../../../domains/lots/models/LotAssetDto";
import { JobIssueVendorDTOSyncable } from "../../../domains/vendors/models/JobIssueVendorDTO";
import { JobSignatureDTOSyncable } from "../../../domains/jobs/models/JobSignatureDTO";
import { UserVendorContactDTOSyncable } from "../../../domains/vendors/models/UserVendorContactDTO";
import { ZoneDTOSyncable } from "../../../domains/zones/models/ZoneDTO";
import { JobAssetDtoSyncable } from "../../../domains/media/models/JobAssetDto";
import { DateUtilsService } from "../../../shared/services/date.service";

@Injectable({
  providedIn: "root",
})
export default class DataSyncMapperService {
  constructor(private dateUtils: DateUtilsService) { }
  mapJob(job: InspectionJob, jobSignatures: JobSignature[], jobIssues: JobIssue[], zones: Zone[], areas: Area[], components: Component[], assertions: Assertion[], assertionIssues: AssertionIssue[], media: Media[]): InspectionJobDTOSyncable {
    const { id, lotId, scheduledDate, isComplete, updatedAt, submittedAt, score, buildQualityStep, updatedBy, streetAddress, divisionId, syncStatus, startedAt } = job;

    const mappedZonesForThisJob: ZoneDTOSyncable[] = zones
      .filter(z => z.lotId === job.lotId)
      .map(zone => this.mapZone(zone, areas, components, assertions, assertionIssues, media));

    const mappedSignaturesForThisJob: JobSignatureDTOSyncable[] = jobSignatures
      .filter(signature => signature.jobId === job.id)
      .map(signature => this.mapJobSignature(signature, media));

    const mappedIssuesForThisJob: JobIssueDTOSyncable[] = jobIssues
      .filter(issue => issue.jobId === job.id)
      .map(issue => this.mapJobIssue(issue, media));

    return {
      id,
      scheduledDate,
      streetAddress,
      salesCommunityName: undefined,
      lotBlock: "",
      lotId,
      externalScheduleTaskId: 0,
      updatedAt,
      updatedBy,
      isComplete,
      submittedAt,
      score,
      buildQualityStep,
      zones: mappedZonesForThisJob,
      signatures: mappedSignaturesForThisJob,
      issues: mappedIssuesForThisJob,
      lot: undefined,
      hasRecoveredIssues: false,
      divisionId,
      syncStatus: syncStatus as SyncableStatus,
      startedAt
    };
  }

  mapJobIssue(issue: JobIssue, media: Media[], jobIssueVendors?: JobIssueVendor[]): JobIssueDTOSyncable {
    const {
      text,
      isComplete,
      identifier,
      isSelfAssigned,
      updatedAt,
      jobId,
      lotAreaIdentifier,
      dueDate,
      completedAt,
      parentIdentifier,
      deleted,
      syncStatus,
      createdAt,
      isConfirmedByHomeowner,
      confirmedByHomeownerAt,
      postCompleteStatus
    } = issue;

    const mappedMediaForThisIssue: JobAssetDtoSyncable[] = media
      .filter(m => m.parentIdentifier === issue.identifier)
      .map(this.mapMedia);

    const mappedJobIssueVendorsForThisIssue: JobIssueVendorDTOSyncable[] | undefined = jobIssueVendors
      ?.filter(v => v.issueIdentifier === issue.identifier)
      ?.map(this.mapJobIssueVendor);

    return {
      identifier,
      parentIdentifier,
      jobId,
      text,
      lotAreaIdentifier,
      isComplete,
      isSelfAssigned,
      updatedAt,
      dueDate,
      completedAt,
      jobIssueVendors: mappedJobIssueVendorsForThisIssue ?? [],
      media: mappedMediaForThisIssue,
      deleted: deleted === 1,
      syncStatus,
      createdAt,
      isConfirmedByHomeowner,
      confirmedByHomeownerAt,
      postCompleteStatus
    };
  }

  mapJobIssueVendor(vendor: JobIssueVendor): JobIssueVendorDTOSyncable {
    const { issueVendorIdentifier, externalVendorId, issueIdentifier, updatedAt, createdAt, deleted, isSent, viewedAt, lastSentAt, vendorName, syncStatus } = vendor;
    return {
      issueVendorIdentifier,
      externalVendorId,
      issueIdentifier,
      updatedAt,
      createdAt,
      deleted,
      isSent,
      viewedAt,
      lastSentAt,
      vendorName,
      syncStatus
    };
  }

  mapMedia(media: Media): JobAssetDtoSyncable {
    const { uploaded, resourceUri, fileIdentifier, updatedAt, deleted, syncStatus, imageType, isApprovedToUse } = media;

    return {
      isUploaded: uploaded === 1,
      azureBlobUri: resourceUri ?? "",
      mediaGuid: fileIdentifier,
      updatedAt,
      deleted: deleted === 1,
      syncStatus,
      imageType,
      isApprovedToUse: isApprovedToUse ?? false,
    };
  }

  // Lots join media via their int id (no guid identifier), rather than the identifier pattern used elsewhere.
  mapLot(lot: Lot, media: Media[]): LotAssetDtoSyncable[] {
    return media
      .filter(m => m.parentIdentifier === String(lot.id))
      .map(m => ({
        lotId: lot.id,
        ...this.mapMedia(m)
      }));
  }

  mapVendorContact = (contact: UserVendorContact): UserVendorContactDTOSyncable => {
    const {
      externalVendorId,
      externalFinancialCommunityId,
      email,
      updatedAt,
      deleted,
      syncStatus
    } = contact;

    return {
      id: 0, // The id on the UserVendorContact is a local DB id, so we set it to 0 for syncing
      externalVendorId,
      externalFinancialCommunityId,
      email,
      updatedAt,
      deleted: deleted || false,
      syncStatus
    };
  };

  mapZone(zone: Zone, areas: Area[], components: Component[], assertions: Assertion[], issues: AssertionIssue[], media: Media[]): ZoneDTOSyncable {
    const { id, name, lotId, updatedAt, syncStatus } = zone;

    const mappedAreasForZone = areas
      .filter(a => a.zoneId === zone.id)
      .map(area => this.mapArea(area, components, assertions, issues, media));

    return {
      id,
      name,
      lotId,
      updatedAt,
      areas: mappedAreasForZone,
      syncStatus
    };
  }

  mapArea(area: Area, components: Component[], assertions: Assertion[], allIssues: AssertionIssue[], allMedia: Media[]): AreaDTOSyncable {
    const { id, name, isApplicable, identifier, updatedAt, sortingOrder, syncStatus } = area;

    const mappedComponentsForArea = components.filter(c => c.areaId === area.id)
      .map(component => this.mapComponent(component, assertions, allIssues, allMedia));

    return {
      id,
      name,
      identifier,
      updatedAt,
      isApplicable,
      components: mappedComponentsForArea,
      sortingOrder,
      syncStatus
    };
  }

  mapComponent(component: Component, assertions: Assertion[], allIssues: AssertionIssue[], allMedia: Media[]): ComponentDTOSyncable {
    const { id, identifier, name, updatedAt, syncStatus } = component;

    const mappedAssertionsForThisComponent = assertions
      .filter(a => a.componentId === component.id)
      .map(assertion => this.mapAssertion(assertion, allIssues, allMedia));

    return {
      id,
      name,
      identifier,
      updatedAt,
      assertions: mappedAssertionsForThisComponent,
      syncStatus
    };
  }

  mapAssertion(assertion: Assertion, allIssues: AssertionIssue[], allMedia: Media[]): AssertionDTOSyncable {
    const { id, text, componentId, isComplete, isCcmComplete, isApplicable, note, ccmNote, identifier, updatedAt, syncStatus } = assertion;

    const uniqueIssues = allIssues.filter((issue, index, self) =>
      // Deduplicate assertion issues to avoid duplicates
      index === self.findIndex(i => i.identifier === issue.identifier)
    );

    const issuesForThisAssertion = uniqueIssues
      .filter(i => i.assertionIdentifier === assertion.identifier)
      .map(issue => this.mapAssertionIssue(issue, allMedia));

    const mediaForThisAssertion = allMedia
      .filter(m => m.parentIdentifier === assertion.identifier)
      .map(this.mapMedia);

    return {
      id,
      componentId,
      identifier,
      text,
      isApplicable,
      isComplete,
      isCcmComplete,
      note,
      ccmNote,
      updatedAt,
      issues: issuesForThisAssertion,
      media: mediaForThisAssertion,
      syncStatus
    };
  }

  mapAssertionIssue(issue: AssertionIssue, allMedia: Media[]): AssertionIssueDTOSyncable {
    const {
      identifier, text, isComplete, updatedAt, jobId, media, lotAreaIdentifier,
      isSelfAssigned, dueDate, completedAt, jobIssueVendors, parentIdentifier, deleted, syncStatus, createdAt,
      isConfirmedByHomeowner, confirmedByHomeownerAt, postCompleteStatus
    } = this.mapJobIssue(issue, allMedia);
    const { severity, assertionIdentifier } = issue;

    return {
      identifier,
      parentIdentifier,
      jobId,
      text,
      lotAreaIdentifier,
      isComplete,
      isSelfAssigned,
      updatedAt,
      dueDate,
      completedAt,
      jobIssueVendors,
      media,
      deleted,
      assertionIdentifier,
      severity,
      syncStatus,
      createdAt,
      isConfirmedByHomeowner,
      confirmedByHomeownerAt,
      postCompleteStatus
    };
  }

  mapJobSignature(signature: JobSignature, media: Media[]): JobSignatureDTOSyncable {
    const {
      jobId,
      identifier,
      signerName,
      signerEmail,
      constructionManagerName,
      signedAt,
      signatureSkipType,
      signatureSkipReason,
      updatedAt,
      syncStatus
    } = signature;

    const mappedMediaForThisSignature: JobAssetDtoSyncable[] = media
      .filter(m => m.parentIdentifier === signature.identifier)
      .map(this.mapMedia);

    return {
      jobId,
      identifier,
      signerName,
      signerEmail,
      constructionManagerName,
      signedAt: signedAt ? this.dateUtils.toLocalISOString(signedAt) : undefined,
      signatureSkipType,
      signatureSkipReason,
      updatedAt,
      media: mappedMediaForThisSignature,
      syncStatus
    };
  }
}